/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const caES: LocalizationResource = {
  locale: 'ca-ES',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copiar i tancar',
      formHint: 'Per raons de seguretat, no podràs veure-ho de nou més tard.',
      formTitle: 'Copia la teva clau API "{{name}}" ara',
    },
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: 'Enrere',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: 'Per defecte',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Un altre dispositiu impostor',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'Principal',
  badge__renewsAt: undefined,
  badge__requiresAction: 'Requereix acció',
  badge__startsAt: undefined,
  badge__thisDevice: 'Aquest dispositiu',
  badge__trialEndsAt: undefined,
  badge__unverified: 'No verificat',
  badge__upcomingPlan: undefined,
  badge__userDevice: "Dispositiu de l'usuari",
  badge__you: 'Tu',
  billing: {
    addPaymentMethod__label: 'Afegeix un nou mètode de pagament',
    alwaysFree: 'Sempre gratuït',
    annually: 'Anualment',
    availableFeatures: 'Funcions disponibles',
    billedAnnually: 'Facturat anualment',
    billedMonthlyOnly: 'Només facturat mensualment',
    cancelFreeTrial: 'Cancel·la la prova gratuïta',
    cancelFreeTrialAccessUntil:
      'Si cancel·les ara, continuaràs amb la prova gratuïta del pla {{plan}} fins al {{date}}.',
    cancelFreeTrialTitle: 'Cancel·la la prova gratuïta pel pla {{plan}}?',
    cancelSubscription: 'Cancel·lar la subscripció',
    cancelSubscriptionAccessUntil:
      "Pots continuar utilitzant les funcions de '{{plan}}' fins al {{ date | longDate('ca-ES') }}, després ja no hi tindràs accés.",
    cancelSubscriptionNoCharge: "No se't cobrarà per aquesta subscripció.",
    cancelSubscriptionPastDue:
      "La teva subscripció finalitzarà immediatament i perdràs l'accés a totes les funcions del pla. Se't demanarà que paguis l'import pendent en la teva propera subscripció.",
    cancelSubscriptionTitle: 'Cancel·lar la subscripció {{plan}}?',
    cannotSubscribeMonthly:
      "No pots subscriure't a aquest pla amb pagament mensual. Per subscriure't-hi, has de triar el pagament anual.",
    cannotSubscribeUnrecoverable:
      "No pots subscriure't a aquest pla. La teva subscripció actual és més cara que aquest pla.",
    checkout: {
      description__paymentSuccessful: "El teu pagament s'ha realitzat correctament.",
      description__subscriptionSuccessful: 'La teva nova subscripció està a punt.',
      downgradeNotice:
        'Mantindràs la teva subscripció actual i les seves funcions fins al final del cicle de facturació; després es canviarà a aquesta subscripció.',
      emailForm: {
        subtitle:
          "Abans de completar la compra, has d'afegir una adreça de correu electrònic per a l'enviament dels rebuts.",
        title: 'Afegeix adreça de correu electrònic',
      },
      lineItems: {
        title__freeTrialEndsAt: 'La prova acaba el',
        title__paymentMethod: 'Mètode de pagament',
        title__statementId: 'ID de la factura',
        title__subscriptionBegins: 'La subscripció comença el',
        title__totalPaid: 'Total pagat',
      },
      pastDueNotice: 'La teva subscripció anterior tenia un pagament pendent.',
      perMonth: 'al mes',
      title: 'Pagament',
      title__paymentSuccessful: 'Pagament realitzat amb èxit!',
      title__subscriptionSuccessful: 'Tot a punt!',
      title__trialSuccess: "La prova s'ha iniciat correctament!",
      totalDueAfterTrial: 'Total a pagar quan acabi la prova en {{days}} dies',
    },
    credit: 'Crèdit',
    creditRemainder: 'Crèdit pel temps restant de la teva subscripció actual.',
    defaultFreePlanActive: 'Estàs al pla gratuït',
    free: 'Gratuït',
    getStarted: 'Comença',
    keepFreeTrial: 'Mantén la prova gratuïta',
    keepSubscription: 'Mantén la subscripció',
    manage: 'Gestiona',
    manageSubscription: 'Gestiona la subscripció',
    month: 'Mes',
    monthly: 'Mensual',
    pastDue: 'Pagament pendent',
    pay: 'Paga {{amount}}',
    paymentMethod: {
      applePayDescription: {
        annual: 'Pagament anual',
        monthly: 'Pagament mensual',
      },
      dev: {
        anyNumbers: 'Qualsevol número',
        cardNumber: 'Número de targeta',
        cvcZip: 'CVC, codi postal',
        developmentMode: 'Mode de desenvolupament',
        expirationDate: 'Data de caducitat',
        testCardInfo: 'Informació de targeta de prova',
      },
    },
    paymentMethods__label: 'Mètodes de pagament',
    highlightedPlanBadge: 'Popular',
    pricingTable: {
      billingCycle: 'Cicle de facturació',
      included: 'Inclòs',
    },
    reSubscribe: "Tornar a subscriure's",
    seeAllFeatures: 'Veure totes les funcions',
    startFreeTrial: 'Iniciar la prova gratuïta',
    startFreeTrial__days: 'Inicia la prova gratuïta de {{days}} dies',
    subscribe: 'Subscriu-te',
    subscriptionDetails: {
      beginsOn: 'Comença el',
      currentBillingCycle: 'Cicle de facturació actual',
      endsOn: 'Acaba el',
      firstPaymentAmount: 'Import del primer pagament',
      firstPaymentOn: 'Primer pagament el',
      nextPaymentAmount: 'Import del proper pagament',
      nextPaymentOn: 'Proper pagament el',
      pastDueAt: 'Pagament pendent des de',
      renewsAt: 'Es renova el',
      subscribedOn: 'Subscrit el',
      title: 'Subscripció',
      trialEndsOn: 'La prova acaba el',
      trialStartedOn: 'La prova va començar el',
    },
    subtotal: 'Subtotal',
    switchPlan: 'Canviar a aquest pla',
    switchToAnnual: 'Canviar a anual',
    switchToAnnualWithAnnualPrice: 'Canviar a anual {{currency}}{{price}} / any',
    switchToMonthly: 'Canviar a mensual',
    switchToMonthlyWithPrice: 'Canviar a mensual {{currency}}{{price}} / mes',
    totalDue: 'Total a pagar',
    totalDueToday: 'Total a pagar avui',
    viewFeatures: 'Veure funcions',
    viewPayment: 'Veure pagament',
    year: 'Any',
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle:
        "Contacta amb l'administrador de la teva organització per obtenir permisos per gestionar connexions empresarials.",
      title: 'No tens permís per gestionar connexions empresarials',
    },
    navbar: {
      title: "Configura l'inici de sessió únic (SSO)",
    },
    selectProviderStep: {
      title: 'Seleccioneu un proveïdor',
      subtitle: 'Seleccioneu el proveïdor per al qual configurareu SSO.',
      body: {
        title: "Seleccioneu el vostre proveïdor d'identitat",
        description: 'Us guiarem pel procés de configuració detallat a continuació.',
      },
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: 'Proveïdor SAML personalitzat',
      },
      warning: 'Un cop seleccionat un proveïdor no podreu canviar-lo fins que la configuració hagi finalitzat',
    },
    verifyEmailDomainStep: {
      title: 'Verifica el correu electrònic',
      subtitle: "Verifica l'adreça de correu electrònic on vols habilitar la connexió empresarial.",
      addEmailAddress: {
        formTitle: 'Necessitem el teu correu',
        formSubtitle: 'Per començar necessitem la teva adreça de correu electrònic',
        inputPlaceholder: 'name@company.com',
        inputLabel: 'Adreça de correu electrònic',
      },
      emailCode: {
        formTitle: "Verifica l'adreça de correu electrònic",
        formSubtitle: 'Introdueix el codi de verificació enviat a {{identifier}}',
        resendButton: 'No has rebut el codi? Reenvia',
        verified: {
          title: 'Hem rebut el teu correu',
          subtitle: 'Has verificat la teva adreça de correu electrònic amb el següent correu',
          inputLabel: 'Adreça de correu electrònic verificada',
        },
      },
      domainTaken: {
        title: 'Aquest domini ({{domain}}) ja té una connexió SSO',
        subtitle: "Contacta amb l'administrador de l'aplicació per obtenir accés a través de la connexió existent.",
      },
    },
  },
  createOrganization: {
    formButtonSubmit: 'Crea organització',
    invitePage: {
      formButtonReset: 'Omet',
    },
    title: 'Crea organització',
  },
  dates: {
    lastDay: "Ahir a les {{ date | timeString('en-US') }}",
    next6Days: "{{ date | weekday('en-US','long') }} a les {{ date | timeString('en-US') }}",
    nextDay: "Demà a les {{ date | timeString('en-US') }}",
    numeric: "{{ date | numeric('en-US') }}",
    previous6Days: "El darrer {{ date | weekday('en-US','long') }} a les {{ date | timeString('en-US') }}",
    sameDay: "Avui a les {{ date | timeString('en-US') }}",
  },
  dividerText: 'o',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'Utilitza un altre mètode',
  footerPageLink__help: 'Ajuda',
  footerPageLink__privacy: 'Privacitat',
  footerPageLink__terms: 'Termes',
  formButtonPrimary: 'Continua',
  formButtonPrimary__verify: 'Verifica',
  formFieldAction__forgotPassword: 'Has oblidat la contrasenya?',
  formFieldError__matchingPasswords: 'Les contrasenyes coincideixen.',
  formFieldError__notMatchingPasswords: 'Les contrasenyes no coincideixen.',
  formFieldError__verificationLinkExpired: "L'enllaç de verificació ha caducat. Si us plau, sol·licita un nou enllaç.",
  formFieldHintText__optional: 'Opcional',
  formFieldHintText__slug: "Un slug és un ID llegible per humans que ha de ser únic. Sovint s'utilitza en URL.",
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: 'Codi de seguretat',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Eliminar compte',
  formFieldInputPlaceholder__emailAddress: 'exemple@correu.com',
  formFieldInputPlaceholder__emailAddress_username: "exemple@correu.com o nom d'usuari",
  formFieldInputPlaceholder__emailAddresses: 'exemple@correu.com, exemple2@correu.com',
  formFieldInputPlaceholder__firstName: 'Nom',
  formFieldInputPlaceholder__lastName: 'Cognoms',
  formFieldInputPlaceholder__organizationDomain: 'exemple.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'contacte@exemple.com',
  formFieldInputPlaceholder__organizationName: "Nom de l'organització",
  formFieldInputPlaceholder__organizationSlug: 'la-meva-org',
  formFieldInputPlaceholder__password: 'Contrasenya',
  formFieldInputPlaceholder__phoneNumber: 'Número de telèfon',
  formFieldInputPlaceholder__username: "Nom d'usuari",
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'Clau API',
  formFieldLabel__apiKeyDescription: 'Descripció',
  formFieldLabel__apiKeyExpiration: 'Expiració',
  formFieldLabel__apiKeyName: 'Nom de clau secreta',
  formFieldLabel__automaticInvitations: 'Activa invitacions automàtiques per a aquest domini',
  formFieldLabel__backupCode: 'Codi de seguretat',
  formFieldLabel__confirmDeletion: 'Confirmació',
  formFieldLabel__confirmPassword: 'Confirma la contrasenya',
  formFieldLabel__currentPassword: 'Contrasenya actual',
  formFieldLabel__emailAddress: 'Adreça de correu electrònic',
  formFieldLabel__emailAddress_username: "Adreça de correu electrònic o nom d'usuari",
  formFieldLabel__emailAddresses: 'Adreces de correu electrònic',
  formFieldLabel__firstName: 'Nom',
  formFieldLabel__lastName: 'Cognoms',
  formFieldLabel__newPassword: 'Nova contrasenya',
  formFieldLabel__organizationDomain: 'Domini',
  formFieldLabel__organizationDomainDeletePending: 'Elimina invitacions pendents i suggeriments',
  formFieldLabel__organizationDomainEmailAddress: 'Adreça de correu electrònic de verificació',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Introdueix una adreça de correu electrònic sota aquest domini per rebre un codi i verificar aquest domini.',
  formFieldLabel__organizationName: 'Nom',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: "Nom de la clau d'accés",
  formFieldLabel__password: 'Contrasenya',
  formFieldLabel__phoneNumber: 'Número de telèfon',
  formFieldLabel__role: 'Rol',
  formFieldLabel__signOutOfOtherSessions: 'Tanca la sessió de tots els altres dispositius',
  formFieldLabel__username: "Nom d'usuari",
  impersonationFab: {
    action__signOut: 'Tanca la sessió',
    title: 'Connectat com a {{identifier}}',
  },
  lastAuthenticationStrategy: 'Últim ús',
  maintenanceMode: 'Mode de manteniment',
  membershipRole__admin: 'Administrador',
  membershipRole__basicMember: 'Membre',
  membershipRole__guestMember: 'Convidat',
  organizationList: {
    action__createOrganization: 'Crea organització',
    action__invitationAccept: 'Uneix-te',
    action__suggestionsAccept: 'Sol·licita unir-te',
    createOrganization: 'Crea Organització',
    invitationAcceptedLabel: 'Unit',
    subtitle: 'per continuar a {{applicationName}}',
    suggestionsAcceptedLabel: 'Aprovació pendent',
    title: 'Trieu un compte',
    titleWithoutPersonal: 'Trieu una organització',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'Invitacions automàtiques',
    badge__automaticSuggestion: 'Suggeriments automàtics',
    badge__manualInvitation: 'Sense inscripció automàtica',
    badge__unverified: 'No verificat',
    billingPage: {
      paymentHistorySection: {
        empty: 'No hi ha historial de pagaments',
        notFound: "No s'ha trobat l'intent de pagament",
        tableHeader__amount: 'Import',
        tableHeader__date: 'Data',
        tableHeader__status: 'Estat',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Establir com a predeterminat',
        actionLabel__remove: 'Eliminar',
        add: 'Afegir un nou mètode de pagament',
        addSubtitle: 'Afegeix un nou mètode de pagament al teu compte.',
        cancelButton: 'Cancel·lar',
        formButtonPrimary__add: 'Afegir mètode de pagament',
        formButtonPrimary__pay: 'Pagar {{amount}}',
        payWithTestCardButton: 'Pagar amb targeta de prova',
        removeMethod: {
          messageLine1: "{{identifier}} s'eliminarà d'aquest compte.",
          messageLine2:
            "Ja no podràs usar aquest mètode de pagament i qualsevol subscripció recurrent que depengui d'ell deixarà de funcionar.",
          successMessage: "{{paymentMethod}} s'ha eliminat del teu compte.",
          title: 'Elimina mètode de pagament',
        },
        title: 'Mètodes de pagament',
      },
      start: {
        headerTitle__payments: 'Pagaments',
        headerTitle__plans: 'Plans',
        headerTitle__statements: 'Factures',
        headerTitle__subscriptions: 'Subscripció',
      },
      statementsSection: {
        empty: 'No hi ha factures per mostrar',
        itemCaption__paidForPlan: 'Pagat pel pla {{plan}} {{period}}',
        itemCaption__proratedCredit: 'Crèdit proporcional pel temps no utilitzat de la subscripció anterior',
        itemCaption__subscribedAndPaidForPlan: 'Subscrit i pagat pel pla {{plan}} {{period}}',
        notFound: 'Factura no trobada',
        tableHeader__amount: 'Import',
        tableHeader__date: 'Data',
        title: 'Factures',
        totalPaid: 'Total pagat',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Gestiona',
        actionLabel__newSubscription: 'Subscriu-te a un pla',
        actionLabel__switchPlan: 'Canvia de pla',
        tableHeader__edit: 'Edita',
        tableHeader__plan: 'Pla',
        tableHeader__startDate: "Data d'inici",
        title: 'Subscripció',
      },
      subscriptionsSection: {
        actionLabel__default: 'Gestiona',
      },
      switchPlansSection: {
        title: 'Canvia de pla',
      },
      title: 'Facturació',
    },
    createDomainPage: {
      subtitle:
        "Afegeix el domini per verificar. Els usuaris amb adreces de correu electrònic en aquest domini poden unir-se a l'organització automàticament o sol·licitar unir-se.",
      title: 'Afegeix domini',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        "Les invitacions no s'han pogut enviar. Ja hi ha invitacions pendents per a les següents adreces de correu electrònic: {{email_addresses}}.",
      formButtonPrimary__continue: 'Envia invitacions',
      selectDropdown__role: 'Selecciona rol',
      subtitle: 'Introdueix o enganxa una o més adreces de correu electrònic, separades per espais o comes.',
      successMessage: "Les invitacions s'han enviat correctament",
      title: 'Convida nous membres',
    },
    membersPage: {
      action__invite: 'Convida',
      action__search: 'Buscar',
      activeMembersTab: {
        menuAction__remove: 'Elimina membre',
        tableHeader__actions: 'Accions',
        tableHeader__joined: 'Unit',
        tableHeader__role: 'Rol',
        tableHeader__user: 'Usuari',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'Estem actualitzant els rols disponibles. Un cop fet, podreu tornar a actualitzar els rols.',
          title: 'Els rols estan temporalment bloquejats',
        },
      },
      detailsTitle__emptyRow: 'No hi ha membres per mostrar',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            "Convida usuaris connectant un domini de correu electrònic amb la teva organització. Qualsevol que es registri amb un domini de correu electrònic coincident podrà unir-se a l'organització en qualsevol moment.",
          headerTitle: 'Invitacions automàtiques',
          primaryButton: 'Gestiona dominis verificats',
        },
        table__emptyRow: 'No hi ha invitacions per mostrar',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Revoca invitació',
        tableHeader__invited: 'Convidat',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Els usuaris que es registren amb un domini de correu electrònic coincident, podran veure una suggerència per sol·licitar unir-se a la teva organització.',
          headerTitle: 'Suggeriments automàtics',
          primaryButton: 'Gestiona dominis verificats',
        },
        menuAction__approve: 'Aprova',
        menuAction__reject: 'Rebutja',
        tableHeader__requested: 'Accés sol·licitat',
        table__emptyRow: 'No hi ha sol·licituds per mostrar',
      },
      start: {
        headerTitle__invitations: 'Invitacions',
        headerTitle__members: 'Membres',
        headerTitle__requests: 'Sol·licituds',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'Gestiona la teva organització.',
      general: 'General',
      members: 'Membres',
      title: 'Organització',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Escriu "{{organizationName}}" a continuació per continuar.',
          messageLine1: 'Estàs segur que vols eliminar aquesta organització?',
          messageLine2: 'Aquesta acció és permanent i irreversible.',
          successMessage: "Has eliminat l'organització.",
          title: 'Elimina organització',
        },
        leaveOrganization: {
          actionDescription: 'Escriu "{{organizationName}}" a continuació per continuar.',
          messageLine1:
            "Estàs segur que vols deixar aquesta organització? Perdràs l'accés a aquesta organització i les seves aplicacions.",
          messageLine2: 'Aquesta acció és permanent i irreversible.',
          successMessage: "Has deixat l'organització.",
          title: 'Deixa organització',
        },
        title: 'Perill',
      },
      domainSection: {
        menuAction__manage: 'Gestiona',
        menuAction__remove: 'Elimina',
        menuAction__verify: 'Verifica',
        primaryButton: 'Afegeix domini',
        subtitle:
          "Permet als usuaris unir-se a l'organització automàticament o sol·licitar unir-se basant-se en un domini de correu electrònic verificat.",
        title: 'Dominis verificats',
      },
      successMessage: "L'organització s'ha actualitzat.",
      title: 'Actualitza perfil',
    },
    removeDomainPage: {
      messageLine1: 'El domini de correu electrònic {{domain}} serà eliminat.',
      messageLine2: "Els usuaris ja no podran unir-se a l'organització automàticament després d'això.",
      successMessage: '{{domain}} ha estat eliminat.',
      title: 'Elimina domini',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: 'Membres',
      profileSection: {
        primaryButton: 'Actualitza perfil',
        title: "Perfil de l'Organització",
        uploadAction__title: 'Logotip',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Eliminar aquest domini afectarà els usuaris convidats.',
        removeDomainActionLabel__remove: 'Elimina domini',
        removeDomainSubtitle: 'Elimina aquest domini dels teus dominis verificats',
        removeDomainTitle: 'Elimina domini',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          "Els usuaris són convidats automàticament a unir-se a l'organització quan es registren i poden unir-se en qualsevol moment.",
        automaticInvitationOption__label: 'Invitacions automàtiques',
        automaticSuggestionOption__description:
          "Els usuaris reben una suggerència per sol·licitar unir-se, però ha de ser aprovat per un administrador abans de poder-se unir a l'organització.",
        automaticSuggestionOption__label: 'Suggeriments automàtics',
        calloutInfoLabel: "Canviar el mode d'inscripció només afectarà els nous usuaris.",
        calloutInvitationCountLabel: 'Invitacions pendents enviades a usuaris: {{count}}',
        calloutSuggestionCountLabel: 'Suggeriments pendents enviats a usuaris: {{count}}',
        manualInvitationOption__description: "Els usuaris només poden ser convidats manualment a l'organització.",
        manualInvitationOption__label: 'Sense inscripció automàtica',
        subtitle: "Trieu com els usuaris d'aquest domini poden unir-se a l'organització.",
      },
      start: {
        headerTitle__danger: 'Perill',
        headerTitle__enrollment: "Opcions d'inscripció",
      },
      subtitle: "El domini {{domain}} ara està verificat. Continua seleccionant el mode d'inscripció.",
      title: 'Actualitza {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Introdueix el codi de verificació enviat al teu correu electrònic',
      formTitle: 'Codi de verificació',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'El domini {{domainName}} necessita ser verificat via correu electrònic.',
      subtitleVerificationCodeScreen:
        "S'ha enviat un codi de verificació a {{emailAddress}}. Introdueix el codi per continuar.",
      title: 'Verifica domini',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'Crea organització',
    action__invitationAccept: 'Uneix-te',
    action__manageOrganization: 'Gestiona',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'Sol·licita unir-te',
    notSelected: "No s'ha seleccionat cap organització",
    personalWorkspace: 'Compte personal',
    suggestionsAcceptedLabel: 'Aprovació pendent',
  },
  paginationButton__next: 'Següent',
  paginationButton__previous: 'Anterior',
  paginationRowText__displaying: 'Mostrant',
  paginationRowText__of: 'de',
  reverification: {
    alternativeMethods: {
      actionLink: 'Provar un altre mètode',
      actionText: 'No tens accés a aquest mètode? Prova una altra opció.',
      blockButton__backupCode: 'Utilitzar codi de seguretat',
      blockButton__emailCode: 'Utilitzar codi de correu electrònic',
      blockButton__passkey: undefined,
      blockButton__password: 'Utilitzar contrasenya',
      blockButton__phoneCode: 'Utilitzar codi de telèfon',
      blockButton__totp: 'Utilitzar verificació TOTP',
      getHelp: {
        blockButton__emailSupport: 'Contactar suport per correu electrònic',
        content:
          "Si no pots verificar la teva identitat amb els mètodes anteriors, posa't en contacte amb el nostre equip de suport.",
        title: 'Necessites ajuda amb la verificació?',
      },
      subtitle: 'Selecciona un dels mètodes disponibles per verificar la teva identitat.',
      title: "Reverificació d'identitat",
    },
    backupCodeMfa: {
      subtitle: "Introdueix el teu codi de seguretat per continuar amb l'accés.",
      title: 'Verificació per codi de seguretat',
    },
    emailCode: {
      formTitle: "Introdueix el codi que t'hem enviat al correu electrònic.",
      resendButton: 'Reenviar codi',
      subtitle: "Comprova el codi de verificació a la teva bústia d'entrada.",
      title: 'Verificació per correu electrònic',
    },
    noAvailableMethods: {
      message: 'Ho sentim, no tens cap mètode de verificació disponible. Contacta amb suport.',
      subtitle: "No s'han trobat mètodes alternatius disponibles.",
      title: 'Mètodes de verificació no disponibles',
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: 'Has oblidat la contrasenya? Recupera-la aquí.',
      subtitle: 'Utilitza la teva contrasenya per verificar la teva identitat.',
      title: 'Verificació per contrasenya',
    },
    phoneCode: {
      formTitle: 'Introdueix el codi enviat al teu telèfon.',
      resendButton: 'Reenvia codi',
      subtitle: 'Rebràs un codi SMS per verificar la teva identitat.',
      title: 'Verificació per telèfon',
    },
    phoneCodeMfa: {
      formTitle: 'Codi de verificació de 2 passos',
      resendButton: 'Reenvia codi',
      subtitle: 'Introdueix el codi de verificació de dos factors enviat al teu telèfon.',
      title: 'Verificació per telèfon (2FA)',
    },
    totpMfa: {
      formTitle: 'Codi TOTP',
      subtitle: "Introdueix el codi d'autenticació TOTP per completar la verificació.",
      title: 'Verificació per TOTP (2FA)',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Afegeix compte',
      action__signOutAll: 'Tanca la sessió de tots els comptes',
      subtitle: 'Selecciona el compte amb el qual vols continuar.',
      title: 'Trieu un compte',
    },
    alternativeMethods: {
      actionLink: 'Obtén ajuda',
      actionText: "No tens cap d'aquests?",
      blockButton__backupCode: 'Utilitza un codi de seguretat',
      blockButton__emailCode: 'Envia codi per correu electrònic a {{identifier}}',
      blockButton__emailLink: 'Envia enllaç per correu electrònic a {{identifier}}',
      blockButton__passkey: "Utilitza la clau d'accés",
      blockButton__password: 'Inicia sessió amb la teva contrasenya',
      blockButton__phoneCode: 'Envia codi SMS a {{identifier}}',
      blockButton__totp: "Utilitza la teva aplicació d'autenticació",
      getHelp: {
        blockButton__emailSupport: 'Suport per correu electrònic',
        content:
          "Si tens dificultats per iniciar sessió al teu compte, envia'ns un correu electrònic i treballarem amb tu per restaurar l'accés tan aviat com sigui possible.",
        title: 'Obtén ajuda',
      },
      subtitle: "Tens problemes? Pots utilitzar qualsevol d'aquests mètodes per iniciar sessió.",
      title: 'Utilitza un altre mètode',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: "El teu codi de seguretat és el que vas obtenir quan vas configurar l'autenticació de dos passos.",
      title: 'Introdueix un codi de seguretat',
    },
    emailCode: {
      formTitle: 'Codi de verificació',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'per continuar a {{applicationName}}',
      title: 'Comprova el teu correu electrònic',
    },
    emailCodeMfa: {
      formTitle: 'Comprova el teu correu electrònic',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'per continuar a {{applicationName}}',
      title: 'Comprova el teu correu electrònic',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      expired: {
        subtitle: 'Torna a la pestanya original per continuar.',
        title: 'Aquest enllaç de verificació ha caducat',
      },
      failed: {
        subtitle: 'Torna a la pestanya original per continuar.',
        title: 'Aquest enllaç de verificació no és vàlid',
      },
      formSubtitle: "Utilitza l'enllaç de verificació enviat al teu correu electrònic",
      formTitle: 'Enllaç de verificació',
      loading: {
        subtitle: 'Seràs redirigit aviat',
        title: 'Iniciant sessió...',
      },
      resendButton: "No has rebut l'enllaç? Reenvia",
      subtitle: 'per continuar a {{applicationName}}',
      title: 'Comprova el teu correu electrònic',
      unusedTab: {
        title: 'Pots tancar aquesta pestanya',
      },
      verified: {
        subtitle: 'Seràs redirigit aviat',
        title: 'Has iniciat sessió amb èxit',
      },
      verifiedSwitchTab: {
        subtitle: 'Torna a la pestanya original per continuar',
        subtitleNewTab: 'Torna a la pestanya recentment oberta per continuar',
        titleNewTab: "S'ha iniciat sessió en una altra pestanya",
      },
    },
    emailLinkMfa: {
      formSubtitle: "Utilitzeu l'enllaç de verificació enviat al vostre correu electrònic",
      resendButton: "No heu rebut l'enllaç? Reenviar",
      subtitle: 'per continuar a {{applicationName}}',
      title: 'Comproveu el vostre correu electrònic',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'Codi de restabliment de contrasenya',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'per restablir la teva contrasenya',
      subtitle_email: 'Primer, introdueix el codi enviat al teu ID de correu electrònic',
      subtitle_phone: 'Primer, introdueix el codi enviat al teu telèfon',
      title: 'Restableix la contrasenya',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Restableix la teva contrasenya',
      label__alternativeMethods: 'O bé, inicia sessió amb un altre mètode',
      title: 'Has oblidat la contrasenya?',
    },
    newDeviceVerificationNotice:
      "Estàs iniciant sessió des d'un dispositiu nou. Demanem verificació per mantenir el compte segur.",
    noAvailableMethods: {
      message: "No es pot procedir amb l'inici de sessió. No hi ha cap factor d'autenticació disponible.",
      subtitle: "S'ha produït un error",
      title: 'No es pot iniciar sessió',
    },
    passkey: {
      subtitle: "Utilitza la teva clau d'accés per continuar amb l'autenticació.",
      title: "Clau d'accés",
    },
    password: {
      actionLink: 'Utilitza un altre mètode',
      subtitle: 'Introdueix la contrasenya associada al teu compte',
      title: 'Introdueix la teva contrasenya',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: 'La teva contrasenya ha estat compromesa',
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'Codi de verificació',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'per continuar a {{applicationName}}',
      title: 'Comprova el teu telèfon',
    },
    phoneCodeMfa: {
      formTitle: 'Codi de verificació',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'Per continuar, introdueix el codi de verificació enviat al teu telèfon',
      title: 'Comprova el teu telèfon',
    },
    resetPassword: {
      formButtonPrimary: 'Restableix la contrasenya',
      requiredMessage:
        'Ja existeix un compte amb una adreça de correu electrònic no verificada. Si us plau, restableix la teva contrasenya per seguretat.',
      successMessage: "La teva contrasenya s'ha canviat amb èxit. Iniciant sessió, espera un moment.",
      title: 'Estableix una nova contrasenya',
    },
    resetPasswordMfa: {
      detailsLabel: 'Necessitem verificar la teva identitat abans de restablir la teva contrasenya.',
    },
    start: {
      actionLink: "Registra't",
      actionLink__join_waitlist: "Uneix-te a la llista d'espera",
      actionLink__use_email: 'Utilitza correu electrònic',
      actionLink__use_email_username: "Utilitza correu electrònic o nom d'usuari",
      actionLink__use_passkey: "Utilitza una clau d'accés",
      actionLink__use_phone: 'Utilitza telèfon',
      actionLink__use_username: "Utilitza nom d'usuari",
      actionText: 'No tens un compte?',
      actionText__join_waitlist: "T'agradaria unir-te a la llista d'espera?",
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'Benvingut de nou! Si us plau, inicia sessió per continuar',
      subtitleCombined: undefined,
      title: 'Inicia sessió a {{applicationName}}',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: 'Codi de verificació',
      subtitle: "Per continuar, introdueix el codi de verificació generat per la teva aplicació d'autenticació",
      title: 'Verificació de dos passos',
    },
    web3Solana: {
      subtitle: 'Selecciona una cartera a continuació per iniciar la sessió',
      title: 'Inicia la sessió amb Solana',
    },
  },
  signInEnterPasswordTitle: 'Introdueix la teva contrasenya',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'Inicia sessió',
      actionText: 'Ja tens un compte?',
      subtitle: 'Si us plau, completa els detalls restants per continuar.',
      title: 'Completa els camps que falten',
    },
    emailCode: {
      formSubtitle: 'Introdueix el codi de verificació enviat a la teva adreça de correu electrònic',
      formTitle: 'Codi de verificació',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'Introdueix el codi de verificació enviat al teu correu',
      title: 'Verifica el teu correu electrònic',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      formSubtitle: "Utilitza l'enllaç de verificació enviat a la teva adreça de correu electrònic",
      formTitle: 'Enllaç de verificació',
      loading: {
        title: 'Registrant...',
      },
      resendButton: "No has rebut l'enllaç? Reenvia",
      subtitle: 'per continuar a {{applicationName}}',
      title: 'Verifica el teu correu electrònic',
      verified: {
        title: 'Registre completat amb èxit',
      },
      verifiedSwitchTab: {
        subtitle: 'Torna a la pestanya recentment oberta per continuar',
        subtitleNewTab: 'Torna a la pestanya anterior per continuar',
        title: 'Correu electrònic verificat amb èxit',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'He llegit i accepto la Política de Privacitat',
        label__onlyTermsOfService: 'He llegit i accepto els Termes del Servei',
        label__termsOfServiceAndPrivacyPolicy:
          'He llegit i accepto els {{ termsOfServiceLink || link("Termes del Servei") }} i la {{ privacyPolicyLink || link("Política de Privacitat") }}',
      },
      continue: {
        subtitle: 'En continuar, acceptes les condicions esmentades.',
        title: 'Si us plau, accepta els nostres termes i polítiques per poder continuar',
      },
    },
    phoneCode: {
      formSubtitle: 'Introdueix el codi de verificació enviat al teu número de telèfon',
      formTitle: 'Codi de verificació',
      resendButton: 'No has rebut el codi? Reenvia',
      subtitle: 'Introdueix el codi de verificació enviat al teu telèfon',
      title: 'Verifica el teu telèfon',
    },
    restrictedAccess: {
      actionLink: "Contacta'ns per obtenir més informació",
      actionText: 'Tens problemes? Obtén ajuda',
      blockButton__emailSupport: 'Suport per correu electrònic',
      blockButton__joinWaitlist: "Uneix-te a la llista d'espera",
      subtitle: "L'accés a aquesta funcionalitat està restringit en aquest moment.",
      subtitleWaitlist: "T'has unit a la llista d'espera. Ens posarem en contacte amb tu aviat.",
      title: 'Accés restringit',
    },
    start: {
      actionLink: 'Inicia sessió',
      actionLink__use_email: 'Utilitza correu electrònic',
      actionLink__use_phone: 'Utilitza telèfon',
      actionText: 'Ja tens un compte?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'Benvingut! Si us plau, completa els detalls per començar.',
      subtitleCombined: 'Benvingut! Si us plau, completa els detalls per començar.',
      title: 'Crea el teu compte',
      titleCombined: 'Crea el teu compte',
    },
    web3Solana: {
      subtitle: 'Selecciona una cartera a continuació per registrar-te',
      title: "Registra't amb Solana",
    },
  },
  socialButtonsBlockButton: 'Continua amb {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        "Ja existeix una organització per al nom d'empresa detectat ({{organizationName}}) i {{organizationDomain}}. Uneix-te per invitació.",
    },
    chooseOrganization: {
      action__createOrganization: 'Crear nova organització',
      action__invitationAccept: 'Unir-se',
      action__suggestionsAccept: 'Sol·licitar unir-se',
      subtitle: 'Uniu-vos a una organització existent o creeu-ne una de nova',
      subtitle__createOrganizationDisabled: 'Uniu-vos a una organització existent',
      suggestionsAcceptedLabel: "Pendent d'aprovació",
      title: 'Trieu una organització',
    },
    createOrganization: {
      formButtonReset: 'Cancel·lar',
      formButtonSubmit: 'Continuar',
      formFieldInputPlaceholder__name: 'La meva organització',
      formFieldInputPlaceholder__slug: 'la-meva-organitzacio',
      formFieldLabel__name: 'Nom',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Introduïu els detalls de la vostra organització per continuar',
      title: 'Configureu la vostra organització',
    },
    organizationCreationDisabled: {
      subtitle: "Contacteu amb l'administrador de la vostra organització per obtenir una invitació.",
      title: 'Heu de pertànyer a una organització',
    },
    signOut: {
      actionLink: 'Tancar sessió',
      actionText: 'Sessió iniciada com a {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: "{{email}} ja és membre de l'organització.",
    avatar_file_size_exceeded:
      'La mida del fitxer supera el límit màxim de 10 MB. Si us plau, tria un fitxer més petit.',
    avatar_file_type_invalid: 'El tipus de fitxer no és compatible. Si us plau, puja una imatge JPG, PNG, GIF o WEBP.',
    captcha_invalid:
      "El registre no ha estat exitós a causa de validacions de seguretat fallides. Si us plau, actualitza la pàgina per tornar-ho a intentar o posa't en contacte amb el suport per obtenir més assistència.",
    captcha_unavailable:
      "El registre no ha estat exitós a causa de la validació fallida de bot. Si us plau, actualitza la pàgina per tornar-ho a intentar o posa't en contacte amb el suport per obtenir més assistència.",
    form_code_incorrect: 'El codi introduït no és vàlid. Si us plau, comprova el codi i torna-ho a intentar.',
    form_email_address_blocked:
      'Els serveis de correu electrònic temporal no estan suportats. Si us plau, utilitzeu la vostra adreça de correu electrònic habitual per crear un compte.',
    form_identifier_exists__email_address: 'Ja existeix un compte amb aquesta adreça de correu electrònic.',
    form_identifier_exists__phone_number: 'Ja existeix un compte amb aquest número de telèfon.',
    form_identifier_exists__username: "Ja existeix un compte amb aquest nom d'usuari.",
    form_identifier_not_found: 'No hem trobat cap compte amb aquests detalls.',
    form_new_password_matches_current: undefined,
    form_param_format_invalid: 'Format de paràmetre no vàlid.',
    form_param_format_invalid__email_address: "L'adreça de correu electrònic ha de ser una adreça vàlida.",
    form_param_format_invalid__phone_number: 'El número de telèfon ha de tenir un format internacional vàlid.',
    form_param_max_length_exceeded__first_name: 'El nom no ha de superar els 256 caràcters.',
    form_param_max_length_exceeded__last_name: 'Els cognoms no han de superar els 256 caràcters.',
    form_param_max_length_exceeded__name: 'El nom no ha de superar els 256 caràcters.',
    form_param_nil: 'El valor del camp no pot ser nul. Si us plau, completa aquest camp.',
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: 'Valor no vàlid.',
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: 'La contrasenya introduïda és incorrecta.',
    form_password_length_too_short: 'La teva contrasenya ha de tenir almenys 8 caràcters.',
    form_password_not_strong_enough: 'La teva contrasenya no és prou forta.',
    form_password_or_identifier_incorrect:
      "La contrasenya o l'identificador és incorrecte. Torna-ho a intentar o utilitza un altre mètode.",
    form_password_pwned:
      'Aquesta contrasenya ha aparegut en una filtració i no es pot utilitzar, si us plau, prova una altra contrasenya.',
    form_password_pwned__sign_in: undefined,
    form_password_size_in_bytes_exceeded:
      'La teva contrasenya ha superat el nombre màxim de bytes permesos, si us plau, redueix-la o elimina alguns caràcters especials.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'Contrasenya incorrecta',
    form_username_invalid_character: "El nom d'usuari conté caràcters no vàlids.",
    form_username_invalid_length: "El nom d'usuari ha de tenir entre 3 i 50 caràcters.",
    form_username_needs_non_number_char: "El vostre nom d'usuari ha de contenir almenys un caràcter no numèric.",
    identification_deletion_failed: 'No pots eliminar la teva última identificació.',
    not_allowed_access:
      "L'adreça de correu electrònic o el número de telèfon no es permet registrar-se. Això podria ser degut a l'ús de '+', '=', '#' o '.' a la vostra adreça de correu electrònic, utilitzant un domini connectat amb un servei de correu electrònic temporal o bloquejant-se explícitament. Si creieu que es tracta d'un error, poseu-vos en contacte amb el servei d'assistència.",
    organization_domain_blocked: "Aquest és un domini bloquejat, si us plau utilitza'n un altre.",
    organization_domain_common: "Aquest és un domini habitual, si us plau utilitza'n un altre.",
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded:
      "Has assolit el límit de membres de l'organització, incloses les invitacions pendents.",
    organization_minimum_permissions_needed:
      "Cal que hi hagi almenys un membre de l'organització amb els permisos mínims necessaris.",
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: "Ja existeix una clau d'accés.",
    passkey_not_supported: "Les claus d'accés no són compatibles.",
    passkey_pa_not_supported: "La clau d'accés no és compatible amb l'autenticació de dispositius.",
    passkey_registration_cancelled: "El registre de la clau d'accés ha estat cancel·lat.",
    passkey_retrieval_cancelled: "La recuperació de la clau d'accés ha estat cancel·lada.",
    passwordComplexity: {
      maximumLength: 'menys de {{length}} caràcters',
      minimumLength: '{{length}} o més caràcters',
      requireLowercase: 'una lletra minúscula',
      requireNumbers: 'un número',
      requireSpecialCharacter: 'un caràcter especial',
      requireUppercase: 'una lletra majúscula',
      sentencePrefix: 'La teva contrasenya ha de contenir',
    },
    phone_number_exists: "Aquest número de telèfon ja està en ús. Si us plau, prova'n un altre.",
    session_exists: 'Ja estàs connectat.',
    web3_missing_identifier: undefined,
    web3_signature_request_rejected: 'Has rebutjat la sol·licitud de signatura. Torna-ho a provar per continuar.',
    web3_solana_signature_generation_failed:
      "S'ha produït un error en generar la signatura. Torna-ho a provar per continuar.",
    zxcvbn: {
      couldBeStronger: 'La teva contrasenya funciona, però podria ser més forta. Prova afegint més caràcters.',
      goodPassword: 'La teva contrasenya compleix tots els requisits necessaris.',
      notEnough: 'La teva contrasenya no és prou forta.',
      suggestions: {
        allUppercase: 'Capitalitza algunes, però no totes les lletres.',
        anotherWord: 'Afegeix més paraules que siguin menys comunes.',
        associatedYears: 'Evita anys que estiguin associats amb tu.',
        capitalization: 'Capitalitza més que la primera lletra.',
        dates: 'Evita dates i anys que estiguin associats amb tu.',
        l33t: "Evita substitucions de lletres previsibles com '@' per 'a'.",
        longerKeyboardPattern:
          'Utilitza patrons de teclat més llargs i canvia la direcció de la tipografia diverses vegades.',
        noNeed: 'Pots crear contrasenyes fortes sense utilitzar símbols, números o lletres majúscules.',
        pwned: 'Si utilitzes aquesta contrasenya en un altre lloc, hauries de canviar-la.',
        recentYears: 'Evita anys recents.',
        repeated: 'Evita paraules i caràcters repetits.',
        reverseWords: 'Evita ortografies invertides de paraules comunes.',
        sequences: 'Evita seqüències de caràcters comuns.',
        useWords: 'Utilitza diverses paraules, però evita frases comunes.',
      },
      warnings: {
        common: 'Aquesta és una contrasenya molt utilitzada.',
        commonNames: "Els noms i cognoms comuns són fàcils d'endevinar.",
        dates: "Les dates són fàcils d'endevinar.",
        extendedRepeat: 'Els patrons de caràcters repetits com "abcabcabc" són fàcils d\'endevinar.',
        keyPattern: "Els patrons curts de teclat són fàcils d'endevinar.",
        namesByThemselves: "Els noms o cognoms sols són fàcils d'endevinar.",
        pwned: 'La teva contrasenya va ser exposada per una violació de dades a Internet.',
        recentYears: "Els anys recents són fàcils d'endevinar.",
        sequences: 'Les seqüències de caràcters comuns com "abc" són fàcils d\'endevinar.',
        similarToCommon: 'Això és similar a una contrasenya molt utilitzada.',
        simpleRepeat: 'Els caràcters repetits com "aaa" són fàcils d\'endevinar.',
        straightRow: "Les files rectes de tecles del teclat són fàcils d'endevinar.",
        topHundred: 'Aquesta és una contrasenya molt utilitzada.',
        topTen: 'Aquesta és una contrasenya altament utilitzada.',
        userInputs: 'No hi hauria de tenir cap dada personal ni relacionada amb la pàgina.',
        wordByItself: "Les paraules soles són fàcils d'endevinar.",
      },
    },
  },
  userButton: {
    action__addAccount: 'Afegeix compte',
    action__closeUserMenu: "Tanca el menú d'usuari",
    action__manageAccount: 'Gestiona compte',
    action__openUserMenu: "Obre el menú d'usuari",
    action__signOut: 'Tanca sessió',
    action__signOutAll: 'Tanca sessió de tots els comptes',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: 'Copiat!',
      actionLabel__copy: 'Copia tot',
      actionLabel__download: 'Descarrega .txt',
      actionLabel__print: 'Imprimeix',
      infoText1: "Els codis de seguretat s'activaran per a aquest compte.",
      infoText2:
        "Guarda els codis de seguretat en secret i emmagatzema'ls de manera segura. Pots regenerar els codis de seguretat si sospites que han estat compromesos.",
      subtitle__codelist: "Emmagatzema'ls de manera segura i mantingues-los en secret.",
      successMessage:
        "Ara els codis de seguretat estan activats. Pots utilitzar un d'aquests per iniciar sessió al teu compte, si perds l'accés al teu dispositiu d'autenticació. Cada codi només es pot utilitzar una vegada.",
      successSubtitle:
        "Pots utilitzar un d'aquests per iniciar sessió al teu compte, si perds l'accés al teu dispositiu d'autenticació.",
      title: 'Afegeix verificació amb codi de seguretat',
      title__codelist: 'Codis de seguretat',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'No hi ha historial de pagaments',
        notFound: "No s'ha trobat l'intent de pagament",
        tableHeader__amount: 'Import',
        tableHeader__date: 'Data',
        tableHeader__status: 'Estat',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Estableix com a predeterminat',
        actionLabel__remove: 'Eliminar',
        add: 'Afegeix nou mètode de pagament',
        addSubtitle: 'Afegeix un nou mètode de pagament al teu compte.',
        cancelButton: 'Cancel·lar',
        formButtonPrimary__add: 'Afegeix mètode de pagament',
        formButtonPrimary__pay: 'Pagar {{amount}}',
        payWithTestCardButton: 'Paga amb targeta de prova',
        removeMethod: {
          messageLine1: "{{identifier}} s'eliminarà d'aquest compte.",
          messageLine2:
            "Ja no podràs usar aquest mètode de pagament i qualsevol subscripció recurrent que depengui d'ell deixarà de funcionar.",
          successMessage: "{{paymentMethod}} s'ha eliminat del teu compte.",
          title: 'Elimina mètode de pagament',
        },
        title: 'Mètodes de pagament',
      },
      start: {
        headerTitle__payments: 'Pagaments',
        headerTitle__plans: 'Plans',
        headerTitle__statements: 'Factures',
        headerTitle__subscriptions: 'Subscripció',
      },
      statementsSection: {
        empty: 'No hi ha factures per mostrar',
        itemCaption__paidForPlan: 'Pagat pel pla {{plan}} {{period}}',
        itemCaption__proratedCredit: 'Crèdit proporcional pel temps no utilitzat de la subscripció anterior',
        itemCaption__subscribedAndPaidForPlan: 'Subscrit i pagat pel pla {{plan}} {{period}}',
        notFound: 'Factura no trobada',
        tableHeader__amount: 'Import',
        tableHeader__date: 'Data',
        title: 'Factures',
        totalPaid: 'Total pagat',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Gestionar',
        actionLabel__newSubscription: 'Subscriure un pla',
        actionLabel__switchPlan: 'Canviar de pla',
        tableHeader__edit: 'Editar',
        tableHeader__plan: 'Pla',
        tableHeader__startDate: "Data d'inici",
        title: 'Subscripció',
      },
      subscriptionsSection: {
        actionLabel__default: 'Gestionar',
      },
      switchPlansSection: {
        title: 'Canviar de pla',
      },
      title: 'Facturació',
    },
    connectedAccountPage: {
      formHint: 'Selecciona un proveïdor per connectar el teu compte.',
      formHint__noAccounts: 'No hi ha proveïdors de comptes externs disponibles.',
      removeResource: {
        messageLine1: "{{identifier}} serà eliminat d'aquest compte.",
        messageLine2:
          'Ja no podràs utilitzar aquest compte connectat i qualsevol funcionalitat dependent deixarà de funcionar.',
        successMessage: '{{connectedAccount}} ha estat eliminat del teu compte.',
        title: 'Elimina compte connectat',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: "El proveïdor s'ha afegit al teu compte",
      title: 'Afegeix compte connectat',
    },
    deletePage: {
      actionDescription: 'Escriu "Elimina compte" a continuació per continuar.',
      confirm: 'Elimina compte',
      messageLine1:
        "Esteu segur que voleu suprimir el vostre compte? És possible que es conservin algunes dades associades. Per sol·licitar la supressió completa de les dades, poseu-vos en contacte amb l'assistència.",
      messageLine2: 'Aquesta acció és permanent i irreversible.',
      title: 'Elimina compte',
    },
    emailAddressPage: {
      emailCode: {
        formHint:
          "S'enviarà un correu electrònic que conté un codi de verificació a aquesta adreça de correu electrònic.",
        formSubtitle: 'Introdueix el codi de verificació enviat a {{identifier}}',
        formTitle: 'Codi de verificació',
        resendButton: 'No has rebut el codi? Reenvia',
        successMessage: "El correu electrònic {{identifier}} s'ha afegit al teu compte.",
      },
      emailLink: {
        formHint:
          "S'enviarà un correu electrònic que conté un enllaç de verificació a aquesta adreça de correu electrònic.",
        formSubtitle: "Fes clic a l'enllaç de verificació al correu enviat a {{identifier}}",
        formTitle: 'Enllaç de verificació',
        resendButton: "No has rebut l'enllaç? Reenvia",
        successMessage: "El correu electrònic {{identifier}} s'ha afegit al teu compte.",
      },
      enterpriseSSOLink: {
        formButton: undefined,
        formSubtitle: undefined,
      },
      formHint: undefined,
      removeResource: {
        messageLine1: "{{identifier}} serà eliminat d'aquest compte.",
        messageLine2: 'Ja no podràs iniciar sessió utilitzant aquesta adreça de correu electrònic.',
        successMessage: '{{emailAddress}} ha estat eliminat del teu compte.',
        title: 'Elimina adreça de correu electrònic',
      },
      title: 'Afegeix adreça de correu electrònic',
      verifyTitle: 'Verifica adreça de correu electrònic',
    },
    formButtonPrimary__add: 'Afegeix',
    formButtonPrimary__continue: 'Continua',
    formButtonPrimary__finish: 'Finalitza',
    formButtonPrimary__remove: 'Elimina',
    formButtonPrimary__save: 'Guarda',
    formButtonReset: 'Cancel·la',
    mfaPage: {
      formHint: 'Selecciona un mètode per afegir.',
      title: 'Afegeix verificació en dos passos',
    },
    mfaPhoneCodePage: {
      backButton: 'Utilitza el número existent',
      primaryButton__addPhoneNumber: 'Afegeix número de telèfon',
      removeResource: {
        messageLine1: '{{ identifier }} ja no rebrà codis de verificació quan iniciï sessió.',
        messageLine2: 'El teu compte podria no ser tan segur.Estàs segur que vols continuar ? ',
        successMessage: 'La verificació en dos passos per SMS ha estat eliminada per { { mfaPhoneCode } } ',
        title: 'Elimina la verificació en dos passos',
      },
      subtitle__availablePhoneNumbers:
        'Selecciona un número de telèfon existent per registrar - lo per la verificació en dos passos per SMS o afegeix - ne un de nou.',
      subtitle__unavailablePhoneNumbers:
        'No hi ha números de telèfon disponibles per registrar - los per la verificació en dos passos per SMS, si us plau, afegeix - ne un de nou.',
      successMessage1:
        'Quan iniciïs sessió, necessitaràs introduir un codi de verificació enviat a aquest número de telèfon com un pas addicional.',
      successMessage2:
        "Guarda aquests codis de seguretat i emmagatzema'ls en un lloc segur. Si perds l'accés al teu dispositiu d'autenticació, pots utilitzar els codis de seguretat per iniciar sessió.",
      successTitle: 'La verificació per codi SMS activada',
      title: 'Afegeix verificació per codi SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Escaneja el codi QR en lloc',
        buttonUnableToScan__nonPrimary: 'No pots escanejar el codi QR? ',
        infoText__ableToScan:
          "Configura un nou mètode d'inici de sessió a la teva aplicació d'autenticador i escaneja el següent codi QR per vincular - lo al teu compte.",
        infoText__unableToScan:
          "Configura un nou mètode d'inici de sessió al teu autenticador i introdueix la clau proporcionada a continuació.",
        inputLabel__unableToScan1:
          "Assegura't que els contrasenyes basades en temps o d'ús únic estan habilitats, després finalitza la vinculació del teu compte.",
        inputLabel__unableToScan2:
          "Alternativament, si el teu autenticador suporta URI de TOTP, també pots copiar l'URI completa.",
      },
      removeResource: {
        messageLine1: "Els codis de verificació d'aquest autenticador ja no seran necessaris quan iniciïs sessió.",
        messageLine2: 'El teu compte podria no ser tan segur.Estàs segur que vols continuar? ',
        successMessage: "La verificació en dos passos mitjançant l'aplicació d'autenticació ha estat eliminada.",
        title: 'Elimina la verificació en dos passos',
      },
      successMessage:
        "La verificació en dos passos ara està habilitada.Quan iniciïs sessió, necessitaràs introduir un codi de verificació d'aquest autenticador com un pas addicional.",
      title: "Afegeix aplicació d'autenticació",
      verifySubtitle: 'Introdueix el codi de verificació generat pel teu autenticador',
      verifyTitle: 'Codi de verificació',
    },
    mobileButton__menu: 'Menú',
    navbar: {
      account: 'Perfil',
      apiKeys: undefined,
      billing: undefined,
      description: 'Gestiona la informació del teu compte.',
      security: 'Seguretat',
      title: 'Compte',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: 'Estàs segur que vols eliminar aquest recurs?',
        title: 'Eliminar recurs',
      },
      subtitle__rename: "Introdueix el nou nom per a la clau d'accés.",
      title__rename: "Reanomenar la clau d'accés",
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Es recomana tancar sessió en tots els altres dispositius que hagin utilitzat la teva contrasenya antiga.',
      readonly:
        "Actualment no pots editar la teva contrasenya perquè pots iniciar sessió només a través de la connexió d'empresa.",
      successMessage__set: 'La teva contrasenya ha estat establerta.',
      successMessage__signOutOfOtherSessions: "S'ha tancat sessió en tots els altres dispositius.",
      successMessage__update: 'La teva contrasenya ha estat actualitzada.',
      title__set: 'Estableix contrasenya',
      title__update: 'Actualitza contrasenya',
    },
    phoneNumberPage: {
      infoText:
        "S'enviarà un missatge de text que conté un codi de verificació a aquest número de telèfon. Poden aplicar-se tarifes de missatges i dades.",
      removeResource: {
        messageLine1: "{ { identifier } } serà eliminat d'aquest compte.",
        messageLine2: 'Ja no podràs iniciar sessió utilitzant aquest número de telèfon.',
        successMessage: '{ { phoneNumber } } ha estat eliminat del teu compte.',
        title: 'Elimina número de telèfon',
      },
      successMessage: "{ { identifier } } s'ha afegit al teu compte.",
      title: 'Afegeix número de telèfon',
      verifySubtitle: 'Introdueix el codi de verificació enviat a { { identifier } } ',
      verifyTitle: 'Verifica número de telèfon',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'Mida recomanada 1:1, fins a 10MB.',
      imageFormDestructiveActionSubtitle: 'Elimina',
      imageFormSubtitle: 'Puja',
      imageFormTitle: 'Imatge de perfil',
      readonly: "La informació del teu perfil ha estat proporcionada per la connexió d'empresa i no pot ser editada.",
      successMessage: 'El teu perfil ha estat actualitzat.',
      title: 'Actualitza perfil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Tanca sessió del dispositiu',
        title: 'Dispositius actius',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Torna-ho a intentar',
        actionLabel__reauthorize: 'Autoritza ara',
        destructiveActionTitle: 'Elimina',
        primaryButton: 'Connecta compte',
        subtitle__disconnected: undefined,
        subtitle__reauthorize:
          'Els àmbits requerits han estat actualitzats, i podràs estar experimentant funcionalitat limitada. Si us plau, reautoritza aquesta aplicació per evitar qualsevol problema',
        title: 'Comptes connectats',
      },
      dangerSection: {
        deleteAccountButton: 'Elimina compte',
        title: 'Elimina compte',
      },
      emailAddressesSection: {
        destructiveAction: 'Elimina correu electrònic',
        detailsAction__nonPrimary: 'Estableix com a principal',
        detailsAction__primary: 'Completa la verificació',
        detailsAction__unverified: 'Verifica',
        primaryButton: 'Afegeix adreça de correu electrònic',
        title: 'Adreces de correu electrònic',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Connecta compte',
        title: "Comptes d'empresa",
      },
      headerTitle__account: 'Detalls del perfil',
      headerTitle__security: 'Seguretat',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Regenera',
          headerTitle: 'Codis de seguretat',
          subtitle__regenerate:
            'Obtén un nou conjunt de codis de seguretat segurs. Els codis de seguretat anteriors seran eliminats i no podran ser utilitzats.',
          title__regenerate: 'Regenera codis de seguretat',
        },
        phoneCode: {
          actionLabel__setDefault: 'Estableix com a predeterminat',
          destructiveActionLabel: 'Elimina',
        },
        primaryButton: 'Afegeix verificació en dos passos',
        title: 'Verificació en dos passos',
        totp: {
          destructiveActionTitle: 'Elimina',
          headerTitle: "Aplicació d'autenticació",
        },
      },
      passkeysSection: {
        menuAction__destructive: "Elimina la clau d'accés",
        menuAction__rename: "Reanomena la clau d'accés",
        primaryButton: undefined,
        title: "Claus d'accés",
      },
      passwordSection: {
        primaryButton__setPassword: 'Estableix contrasenya',
        primaryButton__updatePassword: 'Actualitza contrasenya',
        title: 'Contrasenya',
      },
      phoneNumbersSection: {
        destructiveAction: 'Elimina número de telèfon',
        detailsAction__nonPrimary: 'Estableix com a principal',
        detailsAction__primary: 'Completa la verificació',
        detailsAction__unverified: 'Verifica número de telèfon',
        primaryButton: 'Afegeix número de telèfon',
        title: 'Números de telèfon',
      },
      profileSection: {
        primaryButton: 'Actualitza perfil',
        title: 'Perfil',
      },
      usernameSection: {
        primaryButton__setUsername: "Estableix nom d'usuari",
        primaryButton__updateUsername: "Actualitza nom d'usuari",
        title: "Nom d'usuari",
      },
      web3WalletsSection: {
        destructiveAction: 'Elimina cartera',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'Carteres Web3',
        title: 'Carteres Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Selecciona una cartera de Solana per connectar-la al teu compte.',
          title: 'Afegeix una cartera de Solana',
        },
      },
    },
    usernamePage: {
      successMessage: "El teu nom d'usuari ha estat actualitzat.",
      title__set: "Estableix nom d'usuari",
      title__update: "Actualitza nom d'usuari",
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: "{{identifier}} serà eliminat d'aquest compte.",
        messageLine2: 'Ja no podràs iniciar sessió utilitzant aquesta cartera Web3.',
        successMessage: '{{web3Wallet}} ha estat eliminada del teu compte.',
        title: 'Elimina cartera Web3',
      },
      subtitle__availableWallets: 'Selecciona una cartera Web3 per connectar al teu compte.',
      subtitle__unavailableWallets: 'No hi ha carteres Web3 disponibles.',
      successMessage: 'La cartera ha estat afegida al teu compte.',
      title: 'Afegeix cartera Web3',
      web3WalletButtonsBlockButton: 'Connectar la cartera',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Iniciar sessió',
      actionText: 'Ja tens accés?',
      formButton: "Unir-se a la llista d'espera",
      subtitle: "Introdueix la teva adreça de correu electrònic i t'avisarem quan el teu lloc estigui llest",
      title: "Unir-se a la llista d'espera",
    },
    success: {
      message: "Se't redirigirà aviat...",
      subtitle: "T'enviarem un missatge quan el teu lloc estigui llest",
      title: "Gràcies per unir-te a la llista d'espera!",
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Connecta amb {{walletName}}',
    continue: 'Continua amb {{walletName}}',
    noneAvailable:
      'No s\'han detectat carteres Web3 de Solana. Instal·la una {{ solanaWalletsLink || link("wallet extension") }} compatible amb Web3.',
  },
} as const;
